import React, { useState, useEffect } from 'react';
import {
	Modal,
	ModalHeader,
	ModalBody,
	ModalFooter,
	Button,
	Input,
	FormGroup,
	Label,
	Table,
	Badge,
	Spinner,
	Alert,
} from 'reactstrap';
import { toast } from 'react-toastify';
import axios from 'axios';
import { BASE_URL } from '../constants/apiConfig';
import './SaveDashboardModal.css';

// Dashboard Limit Exceeded Modal Component
const DashboardLimitModal = ({ isOpen, toggle, savedDashboards, onDelete }) => {
	const [selectedDashboard, setSelectedDashboard] = useState(null);

	const handleDelete = async () => {
		if (!selectedDashboard) {
			toast.warning('Please select a dashboard to delete');
			return;
		}

		if (!window.confirm(`Are you sure you want to delete "${selectedDashboard.label}"?`)) {
			return;
		}

		try {
			const token = sessionStorage.getItem('token');
			await axios.delete(`${BASE_URL}/api/admin/dashboard/saved/${selectedDashboard.id}`, {
				headers: { Authorization: `Bearer ${token}` },
			});

			toast.success('Dashboard deleted successfully!');
			onDelete();
			setSelectedDashboard(null);
		} catch (error) {
			console.error('Error deleting dashboard:', error);
			toast.error('Failed to delete dashboard');
		}
	};

	return (
		<Modal isOpen={isOpen} toggle={toggle} size="md">
			<ModalHeader toggle={toggle}>
				<i className="bi bi-exclamation-triangle text-warning me-2"></i>
				Dashboard Limit Reached
			</ModalHeader>
			<ModalBody>
				<p>You have reached the maximum limit of 5 saved dashboards.</p>
				<p>To save a new dashboard, please delete one of your existing dashboards first:</p>

				<FormGroup>
					<Label for="selectDashboard">Select Dashboard to Delete</Label>
					<Input
						type="select"
						id="selectDashboard"
						value={selectedDashboard?.id || ''}
						onChange={(e) => {
							const dashboard = savedDashboards.find(d => d.id === parseInt(e.target.value));
							setSelectedDashboard(dashboard);
						}}>
						<option value="">Choose a dashboard...</option>
						{savedDashboards.map((dashboard) => (
							<option key={dashboard.id} value={dashboard.id}>
								{dashboard.label} (Saved: {new Date(dashboard.created_at).toLocaleDateString()})
							</option>
						))}
					</Input>
				</FormGroup>

				{selectedDashboard && (
					<Alert color="warning" className="mt-3">
						<strong>Selected:</strong> {selectedDashboard.label}
						<br />
						<small className="text-muted">
							Saved on {new Date(selectedDashboard.created_at).toLocaleString()}
						</small>
					</Alert>
				)}
			</ModalBody>
			<ModalFooter>
				<Button color="secondary" onClick={toggle}>
					Cancel
				</Button>
				<Button
					color="danger"
					onClick={handleDelete}
					disabled={!selectedDashboard}>
					<i className="bi bi-trash me-2"></i>
					Delete Selected Dashboard
				</Button>
			</ModalFooter>
		</Modal>
	);
};

const SaveDashboardModal = ({ isOpen, toggle, currentDashboardState }) => {
	const [dashboardLabel, setDashboardLabel] = useState('');
	const [savedDashboards, setSavedDashboards] = useState([]);
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [shareData, setShareData] = useState(null); // For sharing after save
	const [showLimitModal, setShowLimitModal] = useState(false);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const [selectedDashboardToDelete, setSelectedDashboardToDelete] = useState(null);

	// Fetch saved dashboards when modal opens
	useEffect(() => {
		if (isOpen) {
			fetchSavedDashboards();
		}
	}, [isOpen]);

	// Listen for open save modal for share event
	useEffect(() => {
		const handleOpenSaveModalForShare = (event) => {
			const { currentDashboardState, selectedUsers, message } = event.detail;
			setShareData({ currentDashboardState, selectedUsers, message });
			setDashboardLabel(`Shared Dashboard - ${new Date().toLocaleString()}`);
			// Open the modal if not already open
			if (!isOpen) {
				window.dispatchEvent(new CustomEvent('openSaveModal'));
			}
		};

		window.addEventListener('openSaveModalForShare', handleOpenSaveModalForShare);

		return () => {
			window.removeEventListener('openSaveModalForShare', handleOpenSaveModalForShare);
		};
	}, [isOpen]);

	const fetchSavedDashboards = async () => {
		setLoading(true);
		try {
			const token = sessionStorage.getItem('token');
			const response = await axios.get(`${BASE_URL}/api/admin/dashboard/saved`, {
				headers: { Authorization: `Bearer ${token}` },
			});

			if (response.data.success) {
				setSavedDashboards(response.data.data);
			}
		} catch (error) {
			console.error('Error fetching saved dashboards:', error);
			toast.error('Failed to load saved dashboards');
		} finally {
			setLoading(false);
		}
	};

	const handleSave = async () => {
		if (!dashboardLabel.trim()) {
			toast.warning('Please enter a dashboard label');
			return;
		}

		setSaving(true);
		try {
			const token = sessionStorage.getItem('token');
			// Use the current dashboard state passed from the parent (Dashboard)
			const stateToSave = currentDashboardState || {};

			const response = await axios.post(
				`${BASE_URL}/api/admin/dashboard/save`,
				{
					label: dashboardLabel.trim(),
					state: stateToSave,
				},
				{
					headers: { Authorization: `Bearer ${token}` },
				}
			);

			if (response.data.success) {
				toast.success('Dashboard saved successfully!');
				setDashboardLabel('');
				fetchSavedDashboards();

				// If this save was triggered for sharing, proceed to share
				if (shareData) {
					const { selectedUsers, message } = shareData;
					const savedDashboard = response.data.data;

					// Emit event to open share modal with the saved dashboard
					window.dispatchEvent(
						new CustomEvent('openShareModal', { detail: savedDashboard })
					);

					// Reset share data
					setShareData(null);
					toggle();
				}
			} else if (response.data.errorCode === 'MAX_SAVED_DASHBOARDS') {
				// Show limit modal instead of toast
				setShowLimitModal(true);
				return;
			} else {
				throw new Error('Failed to save dashboard');
			}
		} catch (error) {
			console.error('Error saving dashboard:', error);
			if (error.response?.data?.errorCode === 'MAX_SAVED_DASHBOARDS') {
				// Show limit modal instead of toast
				setShowLimitModal(true);
				return;
			}
			toast.error(error.response?.data?.message || 'Failed to save dashboard');
		} finally {
			setSaving(false);
		}
	};

	const handleView = (dashboard) => {
		// Apply the saved dashboard state
		if (dashboard.state && typeof dashboard.state === 'object') {
			// Emit event to parent to apply the state
			window.dispatchEvent(
				new CustomEvent('applyDashboardState', { detail: dashboard })
			);
			toast.success(`Applied dashboard: ${dashboard.label}`);
			toggle();
		}
	};

	const handleCopy = async (dashboard) => {
		try {
			const dashboardUrl = `${window.location.origin}/admin/dashboard?state=${dashboard.id}`;
			await navigator.clipboard.writeText(dashboardUrl);
			toast.success('Dashboard link copied to clipboard!');
		} catch (error) {
			toast.error('Failed to copy link');
		}
	};

	const handleDelete = (id) => {
		setSelectedDashboardToDelete(id);
		setShowDeleteConfirm(true);
	};

	const confirmDelete = async () => {
		if (!selectedDashboardToDelete) return;

		try {
			const token = sessionStorage.getItem('token');
			const response = await axios.delete(`${BASE_URL}/api/admin/dashboard/saved/${selectedDashboardToDelete}`, {
				headers: { Authorization: `Bearer ${token}` },
			});

			if (response.data.success) {
				toast.success('Dashboard deleted successfully!');
				fetchSavedDashboards();
			}
		} catch (error) {
			console.error('Error deleting dashboard:', error);
			toast.error('Failed to delete dashboard');
		} finally {
			setShowDeleteConfirm(false);
			setSelectedDashboardToDelete(null);
		}
	};

	const cancelDelete = () => {
		setShowDeleteConfirm(false);
		setSelectedDashboardToDelete(null);
	};

	const handleShare = (dashboard) => {
		// Emit event to open share modal with pre-selected dashboard
		window.dispatchEvent(
			new CustomEvent('openShareModal', { detail: dashboard })
		);
		toggle();
	};

	const formatDate = (dateString) => {
		return new Date(dateString).toLocaleString('en-US', {
			month: 'short',
			day: 'numeric',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		});
	};

	const handleLimitModalClose = () => {
		setShowLimitModal(false);
	};

	const handleLimitModalDelete = () => {
		fetchSavedDashboards();
		setShowLimitModal(false);
	};

	return (
		<>
			<Modal isOpen={isOpen} toggle={toggle} size="xl" className="save-dashboard-modal">
				<ModalHeader toggle={toggle}>Save Dashboard State</ModalHeader>
				<ModalBody>
					{/* Save New Dashboard Section */}
					<div className="saved-dashboards-section">
						<FormGroup>
							<Label for="dashboardLabel">Dashboard Label</Label>
							<Input
								type="text"
								id="dashboardLabel"
								placeholder="Enter a descriptive label (e.g., Q4 Applications Review)"
								value={dashboardLabel}
								onChange={(e) => setDashboardLabel(e.target.value)}
								onKeyPress={(e) => e.key === 'Enter' && handleSave()}
								maxLength={100}
							/>
						</FormGroup>
						<Button color="primary" onClick={handleSave} disabled={saving || !dashboardLabel.trim()}>
							{saving ? (
								<>
									<Spinner size="sm" className="me-2" />
									Saving...
								</>
							) : (
								<>
									<i className="bi bi-save me-2"></i>
									Save Dashboard
								</>
							)}
						</Button>
					</div>

					{/* Previously Saved Dashboards Section */}
					<div className="saved-dashboards-section">
						<h5 className="section-title">Previously Saved Dashboards</h5>

						{loading ? (
							<div className="text-center py-4">
								<Spinner color="primary" />
								<p className="mt-2">Loading saved dashboards...</p>
							</div>
						) : savedDashboards.length === 0 ? (
							<Alert color="info" className="text-center">
								<i className="bi bi-info-circle me-2"></i>
								No saved dashboards yet. Save your first dashboard state above!
							</Alert>
						) : (
							<div className="table-responsive">
								<Table hover className="saved-dashboards-table">
									<thead>
										<tr>
											<th>Label</th>
											<th>Saved On</th>
											<th>Filters Applied</th>
											<th className="text-center">Actions</th>
										</tr>
									</thead>
									<tbody>
										{savedDashboards.map((dashboard) => (
											<tr key={dashboard.id}>
												<td>
													<strong>{dashboard.label}</strong>
												</td>
												<td className="text-muted">
													{formatDate(dashboard.created_at)}
												</td>
												<td>
													{dashboard.state?.filters && (
														<div className="filters-badges">
															{Object.entries(dashboard.state.filters).map(([key, value]) => {
																if (Array.isArray(value) && value.length > 0) {
																	return (
																		<Badge key={key} color="secondary" pill className="me-1 mb-1">
																			{key}: {value.length}
																		</Badge>
																	);
																}
																return null;
															})}
														</div>
													)}
												</td>
												<td className="text-center">
													<div className="action-button">
														<Button
															color="primary"
															size="sm"
															outline
															onClick={() => handleView(dashboard)}
															title="View/Apply Dashboard">
															<i className="bi bi-eye"></i>
														</Button>
														<Button
															color="info"
															size="sm"
															outline
															onClick={() => handleCopy(dashboard)}
															title="Copy Dashboard Link">
															<i className="bi bi-clipboard"></i>
														</Button>
														<Button
															color="success"
															size="sm"
															outline
															onClick={() => handleShare(dashboard)}
															title="Share Dashboard">
															<i className="bi bi-share"></i>
														</Button>
														<Button
															color="danger"
															size="sm"
															outline
															onClick={() => handleDelete(dashboard.id)}
															title="Delete Dashboard">
															<i className="bi bi-trash"></i>
														</Button>
													</div>
												</td>
											</tr>
										))}
									</tbody>
								</Table>
							</div>
						)}
					</div>
				</ModalBody>
				<ModalFooter>
					<Button color="secondary" onClick={toggle}>
						Close
					</Button>
				</ModalFooter>
			</Modal>

			{/* Dashboard Limit Modal */}
			<DashboardLimitModal
				isOpen={showLimitModal}
				toggle={handleLimitModalClose}
				savedDashboards={savedDashboards}
				onDelete={handleLimitModalDelete}
			/>

			{/* Delete Confirmation Modal */}
			<Modal isOpen={showDeleteConfirm} toggle={cancelDelete} size="sm" centered>
				<ModalHeader toggle={cancelDelete}>
					<i className="bi bi-exclamation-triangle text-warning me-2"></i>
					Confirm Delete
				</ModalHeader>
				<ModalBody>
					<p>Are you sure you want to delete the dashboard <strong>"{selectedDashboardToDelete?.label}"</strong>?</p>
					<p className="text-muted small">This action cannot be undone.</p>
				</ModalBody>
				<ModalFooter>
					<Button color="secondary" onClick={cancelDelete}>
						Cancel
					</Button>
					<Button color="danger" onClick={confirmDelete}>
						<i className="bi bi-trash me-2"></i>
						Delete
					</Button>
				</ModalFooter>
			</Modal>
		</>
	);
};

export default SaveDashboardModal;
